import { keyBy } from 'lodash/fp';

import { Account } from 'dashboard/models/account';
import { Case } from 'dashboard/models/case';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { Report } from 'dashboard/models/report';
import { Tag } from 'dashboard/models/tag';
import { Transaction } from 'dashboard/models/transaction';
import { TransactionLabel } from 'dashboard/models/transaction-label';
import { TransactionStatus } from 'dashboard/models/transaction-status';
import { TransactionType } from 'dashboard/models/transaction-type';
import { User } from 'dashboard/models/user';
import { Verification } from 'dashboard/models/verification';
import { pickListRelationship } from 'dashboard/redux/thunk-actions/transaction';
import { ApiResponse } from 'lib/interfaces/api';
import { pickRelationsFromAPIResponse, Relations } from 'lib/relations';

export interface TransactionWithRelatedData {
  transaction: Transaction | null;
  cases: Case[];
  environment: Environment | null;
  redactor: User | null;
  relatedAccounts: Account[];
  relatedCases: Case[];
  relatedInquiries: Inquiry[];
  relatedReports: Report[];
  relatedTransactions: Transaction[];
  relatedVerifications: Verification[];
  relations: Relations;
  tags: Tag[];
  transactionLabel: TransactionLabel | null;
  transactionStatuses: TransactionStatus[];
  transactionType: TransactionType | null;
}

export const mapRelatedTransactionData = (
  transactionResponse: ApiResponse<Transaction, object>,
) => {
  const { data, included } = transactionResponse;

  const includedItems = keyBy('id', included);

  const currentEnvironment = includedItems[data.relationships.environment.data.id] as Environment;

  const redactor = data.relationships.redactor.data
    ? (includedItems[data.relationships.redactor.data.id] as User)
    : null;

  const transactionLabel = data.relationships['transaction-label'].data
    ? (includedItems[data.relationships['transaction-label'].data.id] as TransactionLabel)
    : null;

  const transactionType = includedItems[
    data.relationships['transaction-type'].data.id
  ] as TransactionType;

  const transactionStatuses = transactionType.relationships.statuses.data.map(
    ({ id }) => includedItems[id] as TransactionStatus,
  );

  const listMatches = pickListRelationship<ListMatch>(transactionResponse, 'list-matches');

  const lists = listMatches.map(
    (listMatch) => includedItems[listMatch.relationships.list.data.id],
  ) as List[];

  const listItems = listMatches.map(
    (listMatch) => includedItems[listMatch.relationships['list-item'].data.id],
  ) as ListItem[];

  return {
    cases: pickListRelationship<Case>(transactionResponse, 'cases', ['assignee', 'objects']),
    currentEnvironment,
    redactor,
    relatedAccounts: pickListRelationship<Account>(transactionResponse, 'related-accounts', [
      'account-type',
    ]),
    relatedCases: pickListRelationship<Case>(transactionResponse, 'related-cases', [
      'assignee',
      'objects',
    ]),
    relatedInquiries: pickListRelationship<Inquiry>(transactionResponse, 'related-inquiries', [
      'documents',
      'inquiry-template',
      'inquiry-template-version',
      'tags',
      'template',
      'verifications',
    ]),
    relatedReports: pickListRelationship<Report>(transactionResponse, 'related-reports'),
    relatedTransactions: pickListRelationship<Transaction>(
      transactionResponse,
      'related-transactions',
      ['transaction-status', 'transaction-type'],
    ),
    relatedVerifications: pickListRelationship<Verification>(
      transactionResponse,
      'related-verifications',
      ['verification-template', 'document'],
    ),
    transactionTags: pickListRelationship<Tag>(transactionResponse, 'tags'),
    transactionLabel,
    transactionType,
    transactionStatuses,
    relations: pickRelationsFromAPIResponse(transactionResponse),
    listMatches: keyBy('id', listMatches),
    lists: keyBy('id', lists),
    listItems: keyBy('id', listItems),
  };
};
